/**
 * "How long ago was this" — the ONE formatter for a past moment shown as a
 * compact label (`Just now` / `12m ago` / `5h ago` / a date once it is older
 * than a day). Shared by the profile header's "Last active" and the game
 * page's "Recently played by" rail; a second copy would drift the moment one
 * of them gained a rung.
 *
 * Distinct from, and deliberately not folded into, `presenceStatus`
 * (@djgg/creations): that model answers "is this person around RIGHT NOW",
 * hides anything older than yesterday, and reads as a sentence. This one
 * labels an ordinary point in the past, however far back, in as few characters
 * as a row will give it.
 *
 * `now` is injected for deterministic tests.
 */
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

export function formatTimeAgo(timestamp: number, now: number = Date.now()): string {
  // A clock-skewed future stamp reads as the present, never as a negative age.
  const elapsed = Math.max(0, now - timestamp);
  if (elapsed < MINUTE_MS) return 'Just now';
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m ago`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h ago`;
  return new Date(timestamp).toLocaleDateString();
}
